import { useEffect, useRef, useState } from "react"
import { Box, Button, ClickAwayListener } from "@mui/material"
import Iconify from "minimals-template/components/Iconify"
import { If } from "lib/@components/switch"
import { ManageColumnsPanel } from "lib/@components/managed-columns/manage-columns-panel"

const DEFAULT_PANEL_SX = {
    display: "flex",
    position: "absolute",
    top: "100%",
    right: 0,
    width: 240,
    maxHeight: 500,
    flexDirection: "column",
    overflow: "hidden",
    cursor: "default",
    lineHeight: "normal",
    textAlign: "left",
    color: "text.primary",
    bgcolor: "background.paper",
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 1,
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
    zIndex: 1000,
}

export function ColumnManagerButton({
    icon = "tabler:columns-3-filled",
    label = "Columns",
    buttonSx,
    panelSx = DEFAULT_PANEL_SX,
    dataCy = "column-manager-button",
}) {
    const [open, setOpen] = useState(false)
    const buttonRef = useRef(null)
    const panelRef = useRef(null)
    const hasOpenedRef = useRef(false)

    useEffect(focusOnOpenChange, [open])

    return (
        <ClickAwayListener onClickAway={close}>
            <Box sx={{ position: "relative", flexShrink: 0 }}>
                <Button
                    ref={buttonRef}
                    size="small"
                    variant="text"
                    startIcon={<Iconify icon={icon} width={16} />}
                    aria-label={open ? "Close column management" : "Manage columns"}
                    aria-expanded={open}
                    onClick={toggle}
                    onKeyDown={handleButtonKeyDown}
                    sx={{ fontSize: 13, py: "5px", ...buttonSx }}
                    data-cy={dataCy}
                >
                    {label}
                </Button>
                <If value={open}>
                    <Box ref={panelRef} onClick={stopPropagation} onKeyDown={handlePanelKeyDown} sx={panelSx}>
                        <ManageColumnsPanel onClose={toggle} />
                    </Box>
                </If>
            </Box>
        </ClickAwayListener>
    )

    function toggle() {
        setOpen((v) => !v)
    }

    function close() {
        if (open) setOpen(false)
    }

    function stopPropagation(e) {
        e.stopPropagation()
    }

    function handleButtonKeyDown(e) {
        if (e.key === " " || e.key === "Enter") {
            e.preventDefault()
            toggle()
        }
    }

    function handlePanelKeyDown(e) {
        if (e.key === " " || e.key === "Enter") e.stopPropagation()
        if (e.key === "Escape") {
            e.stopPropagation()
            toggle()
        }
    }

    function focusOnOpenChange() {
        if (open) {
            hasOpenedRef.current = true
            const first = panelRef.current?.querySelector('button, [tabindex="0"]')
            first?.focus()
        } else if (hasOpenedRef.current) {
            hasOpenedRef.current = false
            buttonRef.current?.focus()
        }
    }
}
